import { FontSize } from '@store/types/UserStoreSlice.type';
import colors from '@theme/colors';
import { ThemeMode } from '@theme/palettes/PaletteOptions.interface';
import { halloweenPalette, aprilFoolsPalette } from '@theme/palettes/themes/customTheme';
import { darkPalette } from '@theme/palettes/themes/darkTheme';
import { standardLightPalette, standardDarkPalette, standardLioTheme } from '@theme/palettes/themes/lioStandardTheme';

export enum HolidayMode {
  AprilFools = 'AprilFools',
  Halloween = 'Halloween',
  Easter = 'Easter',
}

/**
 * Calculate Easter Sunday for a given year using the Anonymous Gregorian algorithm.
 */
const getEasterDate = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

/**
 * Easter date visibility:
 * - 2026: Easter day (April 5) through 1 week after (April 12)
 * - All other years: 1 week before Easter through Easter day
 */
const isEasterVisible = (date: Date): boolean => {
  const year = date.getFullYear();
  const easter = getEasterDate(year);
  const today = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (year === 2026) {
    // Easter day + 1 week after
    const end = new Date(easter);
    end.setDate(end.getDate() + 7);
    return today >= easter && today <= end;
  } else {
    // 1 week before + Easter day
    const start = new Date(easter);
    start.setDate(start.getDate() - 7);
    return today >= start && today <= easter;
  }
};

export const holidayConfig: {
  [key in HolidayMode]: {
    dateVisible?: (date: Date) => boolean;
    title: string;
  };
} = {
  [HolidayMode.AprilFools]: {
    title: 'April 1st',
    dateVisible: (date: Date) => date.getMonth() === 3 && date.getDate() === 1,
  },
  [HolidayMode.Halloween]: {
    title: 'Halloween',
    dateVisible: (date: Date) => date.getMonth() === 9 && date.getDate() >= 17 && date.getDate() <= 31,
  },
  [HolidayMode.Easter]: {
    title: 'Easter',
    dateVisible: isEasterVisible,
  },
};

export const getTodaysHoliday = (): HolidayMode | null => {
  const earlyHolidayMode = window.lio.getEarlyHolidayMode();

  if (earlyHolidayMode && holidayConfig[earlyHolidayMode]) {
    return earlyHolidayMode;
  }

  const today = new Date();
  for (const mode of Object.keys(holidayConfig) as HolidayMode[]) {
    const config = holidayConfig[mode];
    if (config.dateVisible && config.dateVisible(today)) {
      return mode;
    }
  }
  return null;
};

export const getPalette = (mode: ThemeMode, fontSize = FontSize.DEFAULT) => {
  const commonForAllThemes = {
    common: {
      black: colors.BLACK_100,
      white: colors.WHITE_100,
      gray: colors.MEDIUM_GRAY,
    },
    externalIcon: {
      word: colors.MARINE_BLUE,
      excel: colors.GREEN,
    },

    // THEME TODO: figure out if we can remove (or try to actually use them)
    scrollBar: {
      thumbBackground: colors.MEDIUM_GRAY,
      trackBackground: colors.LIGHT_GRAY,
      scrollBarColor: `${colors.LIGHT_GRAY} ${colors.MEDIUM_GRAY} `,
    },
  };

  const holiday = getTodaysHoliday();
  const disableHolidayModeFlag = window.lio.getHolidayModeFlag();

  if (holiday && !disableHolidayModeFlag) {
    if (holiday === HolidayMode.Halloween) {
      // this is just a third theme to play with / see how things are changing
      return {
        mode: 'dark',
        fontSize,
        theme: 'halloweenPalette',
        ...halloweenPalette,
        // in all themes for now
        ...commonForAllThemes,
      };
    } else if (holiday === HolidayMode.AprilFools) {
      return {
        mode: 'dark',
        fontSize,
        theme: 'aprilFoolsPalette',
        ...aprilFoolsPalette,
        // in all themes for now
        ...commonForAllThemes,
      };
    }
    // Easter uses the normal theme - only eggs + logo change
  }

  if (mode === 'trueDark') {
    // custom dark theme
    return {
      mode: 'dark',
      fontSize,
      theme: 'trueDark',
      ...darkPalette,
      // in all themes for now
      ...commonForAllThemes,
    };
  } else {
    // standard lio theme
    return {
      mode,
      fontSize,
      theme: 'lio',
      background: mode === 'light' ? standardLightPalette.background : standardDarkPalette.background,
      text: mode === 'light' ? standardLightPalette.text : standardDarkPalette.text,
      secondary: mode === 'light' ? standardLightPalette.secondary : standardDarkPalette.secondary,
      button: mode === 'light' ? standardLightPalette.button : standardDarkPalette.button,
      other: mode === 'light' ? standardLightPalette.other : standardDarkPalette.other,
      icon: mode === 'light' ? standardLightPalette.icon : standardDarkPalette.icon,

      // common for both 'lio dark and lio light'
      ...standardLioTheme,

      // in all themes for now
      ...commonForAllThemes,
    };
  }
};
